/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IGroup } from '@fluentui/react/lib/DetailsList';
import { getId } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import { defineMessages } from 'react-intl';
import { FilterItem } from 'src/clu/appShell/CLUTestModelsFiltersBlade';
import { LabeledEntities, VerificationSwagger } from 'src/clu/core/services/trainedModels/cluModelVerificationSwagger';
import { SortKeyType } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { generateEntityColorFromString } from 'src/core/utils/color';
import { transformLabelToAnnotation } from 'src/core/utils/labelingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { ModelType } from 'src/luis/types/ModelType';
import { calculateRenderingLevel } from 'src/modules/renderingUtils/renderingUtils';

const messages = defineMessages({
    truePositive: { id: 'clu.cluTestSetDetailsTabUtils.truePositive', defaultMessage: 'True Positive' },
    trueNegative: { id: 'clu.cluTestSetDetailsTabUtils.trueNegative', defaultMessage: 'True Negative' },
    notPredicted: { id: 'clu.cluTestSetDetailsTabUtils.notPredicted', defaultMessage: 'No Prediction' },
    falsePositive: { id: 'clu.cluTestSetDetailsTabUtils.falsePositive', defaultMessage: 'False Positive' },
    falseNegative: { id: 'clu.cluTestSetDetailsTabUtils.falseNegative', defaultMessage: 'False Negative' },
    notLabeled: { id: 'clu.cluTestSetDetailsTabUtils.notLabeled', defaultMessage: 'Unlabeled in test set' }
});

export type CategoryRow = {
    text?: string;
    hasConflict: boolean;
    utteranceName?: string;
    kind: 'Entity' | 'Intent';
    intentExpectedAs?: string;
    intentPredictedAs?: string;
    entityExpectedAs?: LabeledEntities;
    entityPredictedAs?: LabeledEntities;
    predictionText?: ReactIntl.FormattedMessage.MessageDescriptor;
};

export const calculateResultType = (expectedAs: string, predictedAs: string): ReactIntl.FormattedMessage.MessageDescriptor => {
    if (predictedAs) {
        if (expectedAs === predictedAs) {
            return messages.truePositive;
        }

        return messages.falsePositive;
    }

    if (expectedAs) {
        return messages.falseNegative;
    }

    return messages.trueNegative;
};

export const getUniqueNames = (array: string[]): string[] => [...new Set(array)];

export const handleMappedLabels = (
    verificationItem: VerificationSwagger
): { items: CategoryRow[]; isGroupConflict?: boolean; language: string } => {
    const { localize } = useLocalization();

    const mappedGroup = new Map<string, CategoryRow>();
    const groupedItems: { items: CategoryRow[]; isGroupConflict?: boolean; language: string } = {
        items: [],
        isGroupConflict: false,
        language: verificationItem.language
    };

    verificationItem.entitiesResult.expectedEntities.map(item =>
        mappedGroup.set(`${item.category}-${item.length}-${item.offset}`, {
            kind: 'Entity',
            hasConflict: true,
            entityExpectedAs: item,
            utteranceName: verificationItem.text,
            predictionText: messages.falseNegative,
            text: verificationItem.text.substring(item.offset, item.length + item.offset),
            entityPredictedAs: { category: localize(messages.notPredicted), offset: 0, length: 0 }
        })
    );

    verificationItem.entitiesResult.predictedEntities.map(item => {
        const key = `${item.category}-${item.length}-${item.offset}`;
        // Case of has no label but has prediction
        if (!mappedGroup.has(key)) {
            mappedGroup.set(key, {
                kind: 'Entity',
                hasConflict: true,
                entityPredictedAs: item,
                utteranceName: verificationItem.text,
                predictionText: messages.falsePositive,
                text: verificationItem.text.substring(item.offset, item.length + item.offset),
                entityExpectedAs: { category: localize(messages.notLabeled), offset: 0, length: 0 }
            });
        } else {
            const hasConflict = mappedGroup?.get(key).entityExpectedAs.category !== item.category;

            // Case of has label and prediction
            if (mappedGroup.get(key).entityPredictedAs.category !== localize(messages.notPredicted)) {
                groupedItems.items.push({
                    kind: 'Entity',
                    entityExpectedAs: mappedGroup.get(key).entityExpectedAs,
                    entityPredictedAs: item,
                    hasConflict:
                        mappedGroup.get(key).entityExpectedAs.length === 0 ||
                        mappedGroup?.get(key).entityExpectedAs.category !== item.category,
                    predictionText: calculateResultType(mappedGroup.get(key).entityExpectedAs.category, item.category),
                    utteranceName: verificationItem.text,
                    text: verificationItem.text.substring(item.offset, item.length + item.offset)
                });
            } else {
                // Case of has label but has no prediction
                const group = mappedGroup.get(key);
                group.entityPredictedAs = item;
                group.text = verificationItem.text.substring(item.offset, item.length + item.offset);
                group.hasConflict = group.entityExpectedAs.length === 0 || hasConflict;
                group.predictionText = calculateResultType(group.entityExpectedAs.category, group.entityPredictedAs.category);
            }
        }
    });

    mappedGroup.set('', {
        kind: 'Intent',
        intentExpectedAs: verificationItem.intentsResult.expectedIntent,
        intentPredictedAs: verificationItem.intentsResult.predictedIntent,
        hasConflict: verificationItem.intentsResult.expectedIntent !== verificationItem.intentsResult.predictedIntent,
        predictionText: calculateResultType(verificationItem.intentsResult.expectedIntent, verificationItem.intentsResult.predictedIntent)
    });

    groupedItems.items.push(...mappedGroup.values());

    groupedItems.isGroupConflict = groupedItems.items.find(item => item.hasConflict === true) !== undefined;

    return groupedItems;
};

export const isThereAnyFilter = (
    entityLabelsToFilter?: FilterItem[],
    intentLabelsToFilter?: FilterItem[],
    resultsToFilter?: FilterItem[],
    entityPredictionToFilter?: FilterItem[],
    intentPredictionToFilter?: FilterItem[]
) =>
    entityLabelsToFilter?.filter(item => item.isChecked).length > 0 ||
    intentLabelsToFilter?.filter(item => item.isChecked).length > 0 ||
    resultsToFilter?.filter(item => item.isChecked).length > 0 ||
    entityPredictionToFilter?.filter(item => item.isChecked).length > 0 ||
    intentPredictionToFilter?.filter(item => item.isChecked).length > 0;

const areLabelsExistInFilteredItems = (filteredItems: string[], labelText: string) => {
    const labeledWords = [labelText];

    return labeledWords.some(item => filteredItems?.includes(item?.trim()));
};

export const getCheckedItemNames = (items: FilterItem[]) => items?.filter(item => item.isChecked).map(item => item.itemName);

export const canFilterThisItem = (itemToFilter: string, filter: FilterItem[], isFiltered: boolean): boolean => {
    if (!isFiltered) {
        return false;
    }

    const filteredItems = getCheckedItemNames(filter);

    return filteredItems.length > 0 && !areLabelsExistInFilteredItems(filteredItems, itemToFilter);
};

export const handleFilter = (
    labelItemToFilter: string,
    predictionItemToFilter: string,
    labelFilter: FilterItem[],
    predictionFilter: FilterItem[],
    isFiltered?: boolean
): boolean => {
    if (isFiltered && getCheckedItemNames(labelFilter).length === 0 && getCheckedItemNames(predictionFilter).length === 0) {
        return false;
    }

    if (
        canFilterThisItem(labelItemToFilter, labelFilter, isFiltered) ||
        canFilterThisItem(predictionItemToFilter, predictionFilter, isFiltered)
    ) {
        return false;
    }

    return true;
};

export const getTableGroupsAndItems = (
    verificationItems: VerificationSwagger[],
    entityLabelsToFilter?: FilterItem[],
    intentLabelsToFilter?: FilterItem[],
    resultsToFilter?: FilterItem[],
    entityPredictionToFilter?: FilterItem[],
    intentPredictionToFilter?: FilterItem[],
    showMismatchOnly?: boolean,
    searchQuery?: string,
    sortKey?: SortKeyType<CategoryRow>
) => {
    const items: CategoryRow[] = [];
    let groups: IGroup[] = [];
    let groupStartIndex = 0;

    const isFiltered: boolean = isThereAnyFilter(
        entityLabelsToFilter,
        intentLabelsToFilter,
        entityPredictionToFilter,
        intentPredictionToFilter
    );
    const { localize } = useLocalization();

    verificationItems?.forEach((item, index) => {
        const groupedItems: { items: CategoryRow[]; isGroupConflict?: boolean; language: string } = handleMappedLabels(item);

        if (showMismatchOnly) {
            if (!groupedItems.isGroupConflict) {
                groupedItems.items = [];
            } else {
                groupedItems.items = groupedItems?.items?.filter(itemRow => itemRow.hasConflict);
            }
        }

        groupedItems.items = substringSearchStrategy(groupedItems.items, searchQuery, [
            'entityExpectedAs',
            'entityPredictedAs',
            'intentExpectedAs',
            'intentPredictedAs',
            'kind',
            'utteranceName',
            'predictionText'
        ]);

        groupedItems.items = groupedItems?.items?.filter(
            itemRow => !canFilterThisItem(localize(itemRow?.predictionText), resultsToFilter, true)
        );

        groupedItems.items = groupedItems?.items?.filter(itemRow => {
            if (itemRow.kind === 'Entity') {
                return handleFilter(
                    itemRow?.entityExpectedAs.category,
                    itemRow?.entityPredictedAs.category,
                    entityLabelsToFilter,
                    entityPredictionToFilter,
                    isFiltered
                );
            }
            return handleFilter(
                itemRow?.intentExpectedAs,
                itemRow?.intentPredictedAs,
                intentLabelsToFilter,
                intentPredictionToFilter,
                isFiltered
            );
        });

        if (sortKey.key !== 'utteranceName') {
            groupedItems.items = orderBy(groupedItems?.items, [createKeyedStringNormalizer(sortKey.key)], [sortKey.order]);
        }

        if (groupedItems.items.length) {
            groups.push({
                level: 0,
                isCollapsed: true,
                count: groupedItems.items?.length,
                data: groupedItems,
                key: `group: ${index}`,
                name: item.text,
                startIndex: groupStartIndex
            });

            groupStartIndex += groupedItems.items.length;

            items.push(...groupedItems.items);
        }
    });
    if (sortKey.key === 'utteranceName') groups = orderBy(groups, [createKeyedStringNormalizer('name')], [sortKey.order]);

    return { items, groups };
};

export const getTransformedTextToAnnotations = (filteredEntities: LabeledEntities[], isPrediction: boolean) =>
    calculateRenderingLevel(
        filteredEntities?.map(e =>
            transformLabelToAnnotation(
                {
                    depth: 1,
                    renderingLevel: 0,
                    renderedAs: 'line',
                    viewOptions: { isDashed: isPrediction },
                    end: e?.offset + e?.length - 1,
                    start: e?.offset,
                    intrinsicEnd: e?.offset + e?.length - 1,
                    intrinsicStart: e?.offset,
                    entity: { id: getId(), name: e?.category, roles: [], typeId: ModelType.Simple }
                },
                null,
                () => generateEntityColorFromString(e.category)
            )
        )
    );
